import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Form, FormikProvider, useFormik } from 'formik';
import { Box, OutlinedInput, FormHelperText, Button } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { Icon } from '@iconify/react';
import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill';
import { UtilsLib } from '@payface-frontends-libs/utils';
import { verifyOTP } from './services/authJwt';
const { messages } = UtilsLib.Error;
function maxLength(object) {
    if (object.target.value.length > object.target.maxLength) {
        return (object.target.value = object.target.value.slice(0, object.target.maxLength));
    }
}
export function VerifyCodeForm({ verifyPath, otpEndpoint, handleVerify }) {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [codes, setCodes] = useState(['', '', '', '', '', '']);
    const [authOTPState, setAuthOTPState] = useState({
        isAuthenticated: JSON.parse(localStorage.getItem('isAuthenticated')) || false,
        errorVerifyOtp: '',
        isRequesting: false,
    });
    const maxArrayCodesLength = 6;
    const amountToSplice = 1;
    const VerifyCodeSchema = Yup.object().shape({});
    const refInput0 = useRef(null);
    const refInput1 = useRef(null);
    const refInput2 = useRef(null);
    const refInput3 = useRef(null);
    const refInput4 = useRef(null);
    const refInput5 = useRef(null);
    const refIndex0 = 0;
    const refIndex1 = 1;
    const refIndex2 = 2;
    const refIndex3 = 3;
    const refIndex4 = 4;
    const refIndex5 = 5;
    useEffect(() => {
        const { isAuthenticated, errorVerifyOtp } = authOTPState;
        if (isAuthenticated && !errorVerifyOtp) {
            history.push(verifyPath);
            sessionStorage.removeItem('verify');
        }
    }, [authOTPState.isAuthenticated]);
    const handleVerifyOTP = async ({ otp }) => {
        try {
            setAuthOTPState({ ...authOTPState, isRequesting: true });
            await verifyOTP({ otp, otpEndpoint });
            setAuthOTPState({ ...authOTPState, isRequesting: false, errorVerifyOtp: '', isAuthenticated: true });
        }
        catch (error) {
            setCodes(['', '', '', '', '', '']);
            void refInput0?.current?.focus();
            setAuthOTPState({ ...authOTPState, isRequesting: false, errorVerifyOtp: error.message, isAuthenticated: false });
            renderErrorMessage(error.message);
        }
    };
    const formik = useFormik({
        initialValues: {
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        },
        validationSchema: VerifyCodeSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                const otp = `${values.code1}${values.code2}${values.code3}${values.code4}${values.code5}${values.code6}`;
                await handleVerifyOTP({ otp });
                resetForm();
            }
            catch (error) {
                resetForm();
                throw error;
            }
        },
    });
    const { values, errors, isValid, touched, handleSubmit, getFieldProps } = formik;
    const valuesKeys = Object.keys(values);
    const inputPropsParams = {
        maxLength: 1,
        sx: {
            padding: 0,
            textAlign: 'center',
            width: { xs: 36, sm: 56 },
            height: { xs: 36, sm: 56 },
        },
    };
    const testRegexForNumbers = (value) => {
        const regexForNumber = /^[0-9]+[0-9]*$/;
        return regexForNumber.test(value);
    };
    const removeCodeNumber = (index) => {
        const copyOfCodes = [...codes];
        copyOfCodes.splice(index, amountToSplice, '');
        setCodes(copyOfCodes);
    };
    const addCodeNumber = (value, index) => {
        const copyOfCodes = [...codes];
        copyOfCodes.splice(index, amountToSplice, value);
        setCodes(copyOfCodes);
    };
    const checkCurrentValue = (value) => {
        if (testRegexForNumbers(value)) {
            return true;
        }
        return false;
    };
    const goToNextInput = (currentReference, nextReference, index) => {
        const value = currentReference?.current?.value;
        const isValidValue = checkCurrentValue(value);
        if (isValidValue) {
            addCodeNumber(value, index);
            nextReference?.current?.focus();
        }
    };
    const goToPreviousInput = (previousReference, index) => {
        previousReference?.current?.focus();
        previousReference?.current?.select();
        removeCodeNumber(index);
    };
    const handleAutoTab = (event, currentReference, nextReference, previousReference, index) => {
        const { key } = event;
        if (key === 'Backspace') {
            goToPreviousInput(previousReference, index);
        }
        else if (key === ' ' || key === 'Delete') {
            removeCodeNumber(index);
            currentReference?.current?.select();
        }
        else {
            goToNextInput(currentReference, nextReference, index);
        }
    };
    const handleFocus = ({ target }) => {
        target.select();
    };
    const disableButtonCondition = () => {
        if (codes.filter((code) => code !== '').length < maxArrayCodesLength) {
            return true;
        }
        return false;
    };
    const handleInputBox = (keyCode, refInput, nextInput, previousInput, index) => (_jsx(Box, Object.assign({ sx: { mx: 1 } }, { children: _jsx(OutlinedInput, Object.assign({}, getFieldProps(keyCode), { type: "number", autoFocus: keyCode === 'code1', placeholder: "-", onInput: maxLength, inputRef: refInput, onKeyUp: (event) => handleAutoTab(event, refInput, nextInput, previousInput, index), onFocus: (event) => handleFocus(event), error: Boolean(touched[keyCode] && errors[keyCode]), inputProps: inputPropsParams }), void 0) }), void 0));
    const renderErrorMessage = (messageKey) => {
        enqueueSnackbar(messages[messageKey], { variant: 'error' });
    };
    const handleClick = () => {
        handleVerify('');
        sessionStorage.removeItem('verify');
    };
    return (_jsx(Box, Object.assign({ sx: { maxWidth: 480, mx: 'auto' } }, { children: _jsx(FormikProvider, Object.assign({ value: formik }, { children: _jsxs(Form, Object.assign({ autoComplete: "off", noValidate: true, onSubmit: handleSubmit }, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', justifyContent: 'center' } }, { children: [handleInputBox(valuesKeys[0], refInput0, refInput1, refInput0, refIndex0), handleInputBox(valuesKeys[1], refInput1, refInput2, refInput0, refIndex1), handleInputBox(valuesKeys[2], refInput2, refInput3, refInput1, refIndex2), handleInputBox(valuesKeys[3], refInput3, refInput4, refInput2, refIndex3), handleInputBox(valuesKeys[4], refInput4, refInput5, refInput3, refIndex4), handleInputBox(valuesKeys[5], refInput5, refInput5, refInput4, refIndex5)] }), void 0), _jsx(FormHelperText, Object.assign({ error: !isValid, style: { textAlign: 'right' } }, { children: !isValid && 'Code is required' }), void 0), _jsx(Box, Object.assign({ sx: { mt: 4 } }, { children: _jsx(LoadingButton, Object.assign({ fullWidth: true, disabled: disableButtonCondition(), size: "large", type: "submit", variant: "contained", pending: authOTPState.isRequesting }, { children: "Entrar" }), void 0) }), void 0), _jsx(Box, Object.assign({ sx: { mt: 2 } }, { children: _jsx(Button, Object.assign({ fullWidth: true, size: "large", type: "button", variant: "text", onClick: () => handleClick(), startIcon: _jsx(Icon, { icon: arrowIosBackFill, width: 24, height: 24 }, void 0) }, { children: "Voltar" }), void 0) }), void 0)] }), void 0) }), void 0) }), void 0));
}
//# sourceMappingURL=VerifyCodeForm.js.map